import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { Observable, of, switchMap, take } from 'rxjs';

import {
  HeatingConfigurationRegulationProperties,
  Feature,
  FeatureNames,
  FeaturePropertyKey,
  IsStringValueProperty,
  HeatingCurveProperties,
  HeatingCurveCommands,
  DcSensorValueProperties,
  IsNumberValueProperty,
  TemperatureLevelsPerceivedCommands,
  FeatureActionFields,
  FeatureCommands,
  HeatingConfigurationRegulationMode,
} from '@vi-iot/client';

import { InstallationFeaturesStore, UnitsStore } from '@store';
import { convertFahrenheitToCelsius } from 'src/app/shared/common/units';

@Component({
  selector: 'app-circuit-heating-zone-details-overlay',
  templateUrl: './circuit-heating-zone-details-overlay.component.html',
  styleUrls: ['./circuit-heating-zone-details-overlay.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CircuitHeatingZoneDetailsOverlayComponent implements OnInit, OnChanges {
  @Input() circuitId: string;
  readonly featurePropertyKey = FeaturePropertyKey;
  readonly FeatureCommands = FeatureCommands;
  readonly FeatureActionFields = FeatureActionFields;
  readonly HeatingConfigurationRegulationMode = HeatingConfigurationRegulationMode;

  heatingCurve$: Observable<Feature<HeatingCurveProperties, HeatingCurveCommands>>;
  operatingProgramsActive$: Observable<Feature<IsStringValueProperty, never>>;
  circuitHeatingCurvePresets$: Observable<Feature<IsStringValueProperty, never>>;
  circuitConfigurationRegulation$: Observable<
    Feature<HeatingConfigurationRegulationProperties, never>
  >;
  circuitSensorsTemperature$: Observable<Feature<DcSensorValueProperties, never>>;
  operatingProgramName$: Observable<string>;

  targetSupplyTemperature$: Observable<
    Feature<IsNumberValueProperty, TemperatureLevelsPerceivedCommands>
  >;

  private isCelsius: boolean;

  constructor(
    private installationFeaturesStore: InstallationFeaturesStore,
    private unitsStore: UnitsStore
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    const circuitIdChanged = changes.circuitId.currentValue !== changes.circuitId.previousValue;

    if (circuitIdChanged) {
      this.heatingCurve$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
        FeatureNames.circuitHeatingCurve(this.circuitId)
      );
    }
  }

  ngOnInit(): void {
    this.operatingProgramsActive$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.circuitActiveProgram(this.circuitId)
    );
    this.circuitHeatingCurvePresets$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.circuitCurvePresets(this.circuitId)
    );
    this.circuitConfigurationRegulation$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.circuitRegulation(this.circuitId)
    );
    this.circuitSensorsTemperature$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.circuitSupplyTemperature(this.circuitId)
    );
    this.operatingProgramName$ = this.installationFeaturesStore.getCircuitHeatingZoneOperatingProgramName(
      this.circuitId
    );

    this.targetSupplyTemperature$ = this.installationFeaturesStore
      .selectInstallationDeviceFeature<HeatingConfigurationRegulationProperties, never>(
        FeatureNames.callForHeatConfigurationRegulation
      )
      .pipe(
        switchMap((feature) => {
          const isConstantControlled =
            feature?.properties?.mode?.value ===
            HeatingConfigurationRegulationMode.ConstantControlled;

          const targetSupplyTemperature$ = this.installationFeaturesStore.selectInstallationDeviceFeature<
            IsNumberValueProperty,
            TemperatureLevelsPerceivedCommands
          >(FeatureNames.callForHeatTemperature);

          return isConstantControlled ? targetSupplyTemperature$ : of(undefined);
        })
      );

    this.unitsStore.isTemperatureMetric$.pipe(take(1)).subscribe((isCelsius) => {
      this.isCelsius = isCelsius;
    });
  }

  onTargetSupplyTemperatureChange(value: number): void {
    this.installationFeaturesStore.updateFeature(
      FeatureNames.callForHeatTemperature,
      this.isCelsius ? value : Math.round(convertFahrenheitToCelsius(value)),
      FeaturePropertyKey.value,
      FeatureActionFields.temperature,
      FeatureCommands.setTemperature
    );
  }
}
